import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr, inr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, CreditCard, Pencil, Trash2, FileDown, Plus, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar, usePagination } from "@/components/Pagination";

const statusColor = {
  paid: "text-green-500 bg-green-500/10 border-green-500/40",
  partial: "text-yellow-500 bg-yellow-500/10 border-yellow-500/40",
  unpaid: "text-red-500 bg-red-500/10 border-red-500/40",
  revoked: "text-muted-foreground bg-secondary border-border",
};
const PAY_LABELS = { paid: "Paid", partial: "Partially Paid", unpaid: "Pending Payment", revoked: "Revoked" };

export default function Invoices() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [openInv, setOpenInv] = useState(null);
  const [pay, setPay] = useState({ amount: 0, method: "cash" });
  const [editOpen, setEditOpen] = useState(false);
  const [ef, setEf] = useState({ parts: [], labour_lines: [], discount: 0, gst_rate: 18, status: "unpaid" });
  const isStaff = ["owner", "manager", "cashier"].includes(user.role);
  const canModify = ["owner", "manager"].includes(user.role);

  const [filters, setFilters] = useState({ date_from: "", date_to: "", status: "" });
  const [org, setOrg] = useState(null);
  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    api.get(`/invoices?${p}`).then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
    api.get("/vehicles").then(r => setVehicles(r.data)).catch(() => {});
    api.get("/organisation").then(r => { const d = r.data; setOrg(Array.isArray(d) ? (d[0] || null) : d); }).catch(() => {});
  }, []);

  const invTotals = useMemo(() => list.reduce((a, inv) => {
    const parts = inv.parts_total || 0;
    const lab = inv.labour_total || 0;
    return {
      items: a.items + (inv.items_count || 0),
      parts: a.parts + parts, labour: a.labour + lab, total: a.total + parts + lab,
      cash: a.cash + (inv.paid_cash || 0), phonepay: a.phonepay + (inv.paid_phonepay || 0),
      paytm: a.paytm + (inv.paid_paytm || 0),
      paid: a.paid + (inv.paid || 0), balance: a.balance + (inv.due || 0),
    };
  }, { items: 0, parts: 0, labour: 0, total: 0, cash: 0, phonepay: 0, paytm: 0, paid: 0, balance: 0 }), [list]);

  const custName = (id) => customers.find(c => c.id === id)?.name || "-";
  const vehLabel = (id) => {
    const v = vehicles.find(x => x.id === id);
    return v ? `${v.registration} · ${v.make} ${v.model}` : "-";
  };

  const record = async () => {
    try {
      await api.post("/payments", { invoice_id: openInv.id, amount: Number(pay.amount), method: pay.method });
      toast.success("Payment recorded");
      setPay({ amount: 0, method: "cash" });
      load();
      const { data } = await api.get(`/invoices/${openInv.id}`);
      setOpenInv(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const printInv = () => window.print();

  const printList = () => {
    document.body.classList.add("printing-inv-list");
    window.onafterprint = () => document.body.classList.remove("printing-inv-list");
    window.print();
  };
  const exportPdf = async () => {
    try {
      const token = localStorage.getItem("erp_token");
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      const res = await fetch(`${API}/invoices/export.pdf${p.toString() ? "?" + p : ""}`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "invoices_export.pdf"; a.click();
    } catch { toast.error("PDF export failed"); }
  };
  const filterSummary = `Period: ${filters.date_from || "*"} to ${filters.date_to || "*"} · Payment Status: ${filters.status || "All"}`;

  const openEdit = (inv) => {
    setEf({
      parts: (inv.parts || []).map(p => ({ name: p.name, qty: p.qty, price: p.price })),
      labour_lines: (inv.labour_lines || (inv.labour_charges ? [{ name: "Labour Charges", labour_rate: inv.labour_charges }] : []))
        .map(l => ({ name: l.name, labour_rate: l.labour_rate ?? l.rate ?? 0 })),
      discount: inv.discount || 0, gst_rate: inv.gst_rate || 18, status: inv.status,
    });
    setEditOpen(true);
  };
  const setPart = (i, k, v) => setEf(x => ({ ...x, parts: x.parts.map((p, j) => j === i ? { ...p, [k]: v } : p) }));
  const setLab = (i, k, v) => setEf(x => ({ ...x, labour_lines: x.labour_lines.map((l, j) => j === i ? { ...l, [k]: v } : l) }));
  const saveEdit = async () => {
    try {
      const { data } = await api.patch(`/invoices/${openInv.id}`, {
        parts: ef.parts.map(p => ({ name: p.name, qty: Number(p.qty), price: Number(p.price) })),
        labour_lines: ef.labour_lines.map(l => ({ name: l.name, labour_rate: Number(l.labour_rate) })),
        discount: Number(ef.discount), gst_rate: Number(ef.gst_rate), status: ef.status,
      });
      toast.success("Invoice updated");
      setEditOpen(false); setOpenInv(data); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteInv = async (inv) => {
    if (!confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      setOpenInv(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const downloadPdf = async (inv) => {
    try {
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${API}/invoices/${inv.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${inv.invoice_number}.pdf`;
      a.click();
    } catch { toast.error("PDF download failed"); }
  };

  const labourLines = (inv) => inv.labour_lines?.length ? inv.labour_lines
    : (inv.labour_charges ? [{ name: "Labour Charges", labour_rate: inv.labour_charges }] : []);

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Workshop · GST billing</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Invoices</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="inv-export-pdf-btn" variant="outline" size="sm" onClick={exportPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> Export PDF</Button>
          <Button data-testid="inv-print-list-btn" variant="outline" size="sm" onClick={printList} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap mb-4 border border-border bg-card rounded-sm p-3" data-testid="inv-filter-bar">
        <div className="w-[140px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">From</Label>
          <Input data-testid="inv-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
        </div>
        <div className="w-[140px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">To</Label>
          <Input data-testid="inv-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"/>
        </div>
        <div className="w-[150px]"><Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Payment Status</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="inv-filter-status" className="mt-1 rounded-sm bg-secondary border-border h-8 text-xs"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partially Paid</SelectItem>
              <SelectItem value="unpaid">Pending Payment</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th rowSpan={2} className="text-left p-3 align-bottom">Invoice #</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Date</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Customer</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Mobile</th>
              <th rowSpan={2} className="text-right p-3 align-bottom">Items</th>
              <th colSpan={3} className="text-center p-2 border-l border-b border-border/60" data-testid="inv-amount-group-header">Amount</th>
              <th colSpan={4} className="text-center p-2 border-l border-b border-border/60" data-testid="inv-paid-group-header">Paid</th>
              <th rowSpan={2} className="text-right p-3 align-bottom border-l border-border/60">Balance</th>
              <th rowSpan={2} className="text-left p-3 align-bottom">Payment</th>
              <th rowSpan={2} className="text-right p-3 align-bottom">Actions</th>
            </tr>
            <tr>
              <th className="text-right px-3 pb-2 border-l border-border/60">Parts</th>
              <th className="text-right px-3 pb-2">Labour</th>
              <th className="text-right px-3 pb-2">Total</th>
              <th className="text-right px-3 pb-2 border-l border-border/60">Cash</th>
              <th className="text-right px-3 pb-2">PhonePay</th>
              <th className="text-right px-3 pb-2">Paytm</th>
              <th className="text-right px-3 pb-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={15} className="p-6 text-center text-muted-foreground">No invoices</td></tr>}
            {pg.pageItems.map(inv => (
              <tr key={inv.id} data-testid={`inv-row-${inv.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-primary uppercase">{inv.invoice_number}</td>
                <td className="p-3 font-mono text-xs">{(inv.created_at || "").slice(0, 10)}</td>
                <td className="p-3 text-xs">{inv.customer_name || custName(inv.customer_id)}</td>
                <td className="p-3 text-xs font-mono" data-testid={`inv-mobile-${inv.id}`}>{inv.customer_phone || "-"}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`inv-items-${inv.id}`}>{inv.items_count || 0}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`inv-parts-${inv.id}`}>{inr(inv.parts_total || 0)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`inv-labour-${inv.id}`}>{inr(inv.labour_total || 0)}</td>
                <td className="p-3 text-right font-mono font-semibold" data-testid={`inv-total-${inv.id}`}>{inr((inv.parts_total || 0) + (inv.labour_total || 0))}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid={`inv-cash-${inv.id}`}>{inr(inv.paid_cash || 0)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`inv-phonepay-${inv.id}`}>{inr(inv.paid_phonepay || 0)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid={`inv-paytm-${inv.id}`}>{inr(inv.paid_paytm || 0)}</td>
                <td className="p-3 text-right font-mono font-semibold" data-testid={`inv-paid-${inv.id}`}>{inr(inv.paid || 0)}</td>
                <td className={`p-3 text-right font-mono text-xs border-l border-border/60 ${inv.due > 0 ? "text-destructive" : "text-green-500"}`} data-testid={`inv-balance-${inv.id}`}>{inr(inv.due)}</td>
                <td className="p-3"><span data-testid={`inv-paystatus-${inv.id}`} className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[inv.status] || ""}`}>{PAY_LABELS[inv.status] || inv.status}</span></td>
                <td className="p-3 text-right space-x-1">
                  <Button data-testid={`open-inv-${inv.id}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpenInv(inv)}>View</Button>
                  <Button data-testid={`pdf-inv-${inv.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => downloadPdf(inv)} title="Download PDF"><FileDown className="w-3 h-3"/></Button>
                  {canModify && (
                    <>
                      <Button data-testid={`edit-inv-${inv.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setOpenInv(inv); openEdit(inv); }} title="Edit"><Pencil className="w-3 h-3"/></Button>
                      <Button data-testid={`del-inv-${inv.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteInv(inv)} title="Delete"><Trash2 className="w-3 h-3"/></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {list.length > 0 && (
              <tr data-testid="inv-total-row" className="border-t-2 border-primary/40 bg-secondary/60 font-bold align-top">
                <td className="p-3 text-[10px] uppercase tracking-[0.2em] text-primary" colSpan={4}>Totals · {list.length} invoices</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="inv-total-items">{invTotals.items}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid="inv-total-parts">{inr(invTotals.parts)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="inv-total-labour">{inr(invTotals.labour)}</td>
                <td className="p-3 text-right font-mono" data-testid="inv-total-total">{inr(invTotals.total)}</td>
                <td className="p-3 text-right font-mono text-xs border-l border-border/60" data-testid="inv-total-cash">{inr(invTotals.cash)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="inv-total-phonepay">{inr(invTotals.phonepay)}</td>
                <td className="p-3 text-right font-mono text-xs" data-testid="inv-total-paytm">{inr(invTotals.paytm)}</td>
                <td className="p-3 text-right font-mono" data-testid="inv-total-paid">{inr(invTotals.paid)}</td>
                <td className="p-3 text-right font-mono border-l border-border/60" data-testid="inv-total-balance">{inr(invTotals.balance)}</td>
                <td colSpan={2}/>
              </tr>
            )}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>

      {/* -------- View / Pay / Print / PDF dialog -------- */}
      <Dialog open={!!openInv && !editOpen} onOpenChange={(v) => !v && setOpenInv(null)}>
        <DialogContent className="rounded-sm bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          {openInv && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase text-2xl flex items-center justify-between">
                  <span>Invoice {openInv.invoice_number}</span>
                  <div className="flex gap-1 print:hidden">
                    <Button data-testid="inv-print-btn" size="sm" variant="ghost" onClick={printInv} className="text-xs"><Printer className="w-4 h-4 mr-1"/> Print</Button>
                    <Button data-testid="inv-pdf-btn" size="sm" variant="ghost" onClick={() => downloadPdf(openInv)} className="text-xs"><FileDown className="w-4 h-4 mr-1"/> PDF</Button>
                    {canModify && (
                      <>
                        <Button data-testid="inv-edit-btn" size="sm" variant="ghost" onClick={() => openEdit(openInv)} className="text-xs"><Pencil className="w-4 h-4 mr-1"/> Edit</Button>
                        <Button data-testid="inv-del-btn" size="sm" variant="ghost" onClick={() => deleteInv(openInv)} className="text-xs text-destructive"><Trash2 className="w-4 h-4 mr-1"/> Delete</Button>
                      </>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Customer: </span><span className="font-semibold">{custName(openInv.customer_id)}</span></div>
                  <div><span className="text-muted-foreground">Vehicle: </span><span className="font-semibold">{vehLabel(openInv.vehicle_id)}</span></div>
                  <div><span className="text-muted-foreground">Date: </span><span className="font-mono">{(openInv.created_at || "").slice(0, 10)}</span></div>
                  <div><span className="text-muted-foreground">Status: </span><span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${statusColor[openInv.status]}`}>{openInv.status}</span></div>
                </div>

                {/* Itemised labour */}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Labour / Job Types</div>
                  <div className="border border-border rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <tbody>
                        {labourLines(openInv).length === 0 && <tr><td className="p-2 text-muted-foreground italic">No labour charges</td></tr>}
                        {labourLines(openInv).map((l, i) => (
                          <tr key={i} data-testid={`labour-line-${i}`} className="border-b border-border/50 last:border-0">
                            <td className="p-2">{l.name}</td>
                            <td className="p-2 text-right font-mono">{inr(l.labour_rate ?? l.rate ?? 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Parts */}
                {openInv.parts?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Parts Used</div>
                    <div className="border border-border rounded-sm overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-secondary text-[10px] uppercase tracking-wider text-muted-foreground">
                          <tr><th className="text-left p-2">Part</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Rate</th><th className="text-right p-2">Amount</th></tr>
                        </thead>
                        <tbody>
                          {openInv.parts.map((p, i) => (
                            <tr key={i} data-testid={`part-line-${i}`} className="border-b border-border/50 last:border-0">
                              <td className="p-2">{p.name}</td>
                              <td className="p-2 text-right font-mono">{p.qty}</td>
                              <td className="p-2 text-right font-mono">{inr(p.price)}</td>
                              <td className="p-2 text-right font-mono">{inr(p.qty * p.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Totals with GST breakdown */}
                <div className="border-t border-border pt-2 space-y-1 text-xs">
                  {openInv.discount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span className="font-mono text-destructive">-{inr(openInv.discount)}</span></div>}
                  {openInv.loyalty_applied > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Loyalty Redeemed</span><span className="font-mono text-destructive">-{inr(openInv.loyalty_applied)}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{inr(openInv.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">CGST @ {openInv.gst_rate / 2}%</span><span className="font-mono">{inr(openInv.gst_amount / 2)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">SGST @ {openInv.gst_rate / 2}%</span><span className="font-mono">{inr(openInv.gst_amount / 2)}</span></div>
                  <div className="flex justify-between font-display font-black text-lg pt-1"><span>Total</span><span className="font-mono text-primary" data-testid="inv-total">{inr(openInv.total)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span className="font-mono text-green-500">{inr(openInv.paid)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Due</span><span className={`font-mono ${openInv.due > 0 ? "text-destructive" : "text-green-500"}`}>{inr(openInv.due)}</span></div>
                </div>

                {isStaff && openInv.due > 0 && (
                  <div className="border border-primary/30 bg-primary/5 rounded-sm p-3 flex items-end gap-2 print:hidden">
                    <div className="flex-1">
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Record Payment (₹)</Label>
                      <Input data-testid="pay-amount" type="number" value={pay.amount} onChange={(e) => setPay(x => ({ ...x, amount: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
                    </div>
                    <Select value={pay.method} onValueChange={(v) => setPay(x => ({ ...x, method: v }))}>
                      <SelectTrigger data-testid="pay-method" className="rounded-sm bg-secondary border-border w-28"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="phonepay">PhonePay</SelectItem>
                        <SelectItem value="paytm">Paytm</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                        <SelectItem value="online">Online</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button data-testid="record-pay-btn" onClick={record} disabled={!pay.amount || Number(pay.amount) <= 0} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-xs">
                      <CreditCard className="w-3 h-3 mr-1"/> Record
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* -------- Full Edit dialog (Owner/Manager) -------- */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" onKeyDownCapture={handleSimpleFormNav}>
          <DialogHeader><DialogTitle className="font-display uppercase">Edit Invoice {openInv?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            {/* Labour lines */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Labour / Job Types (itemised)</div>
                <Button size="sm" variant="ghost" onClick={() => setEf(x => ({ ...x, labour_lines: [...x.labour_lines, { name: "", labour_rate: 0 }] }))} className="h-6 text-xs text-primary"><Plus className="w-3 h-3 mr-1"/> Add</Button>
              </div>
              {ef.labour_lines.map((l, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <Input data-testid={`elab-name-${i}`} value={l.name} onChange={(e) => setLab(i, "name", e.target.value)} placeholder="Job type" className="rounded-sm bg-secondary border-border flex-1"/>
                  <Input data-testid={`elab-rate-${i}`} type="number" value={l.labour_rate} onChange={(e) => setLab(i, "labour_rate", e.target.value)} placeholder="Rate" className="rounded-sm bg-secondary border-border w-28 text-right"/>
                  <Button size="sm" variant="ghost" onClick={() => setEf(x => ({ ...x, labour_lines: x.labour_lines.filter((_, j) => j !== i) }))} className="h-9 w-9 p-0 text-destructive"><X className="w-3 h-3"/></Button>
                </div>
              ))}
            </div>
            {/* Parts lines */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Parts Used</div>
                <Button size="sm" variant="ghost" onClick={() => setEf(x => ({ ...x, parts: [...x.parts, { name: "", qty: 1, price: 0 }] }))} className="h-6 text-xs text-primary"><Plus className="w-3 h-3 mr-1"/> Add</Button>
              </div>
              {ef.parts.map((p, i) => (
                <div key={i} className="flex gap-2 mb-1">
                  <Input data-testid={`epart-name-${i}`} value={p.name} onChange={(e) => setPart(i, "name", e.target.value)} placeholder="Part name" className="rounded-sm bg-secondary border-border flex-1"/>
                  <Input data-testid={`epart-qty-${i}`} type="number" value={p.qty} onChange={(e) => setPart(i, "qty", e.target.value)} placeholder="Qty" className="rounded-sm bg-secondary border-border w-20 text-right"/>
                  <Input data-testid={`epart-price-${i}`} type="number" value={p.price} onChange={(e) => setPart(i, "price", e.target.value)} placeholder="Rate" className="rounded-sm bg-secondary border-border w-28 text-right"/>
                  <Button size="sm" variant="ghost" onClick={() => setEf(x => ({ ...x, parts: x.parts.filter((_, j) => j !== i) }))} className="h-9 w-9 p-0 text-destructive"><X className="w-3 h-3"/></Button>
                </div>
              ))}
            </div>
            {/* Discount / GST / Status */}
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Discount (₹)</Label><Input data-testid="edit-discount" type="number" value={ef.discount} onChange={(e) => setEf(x => ({ ...x, discount: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border text-right"/></div>
              <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">GST Rate (%)</Label><Input data-testid="edit-gst" type="number" value={ef.gst_rate} onChange={(e) => setEf(x => ({ ...x, gst_rate: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border text-right"/></div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Payment Status</Label>
                <Select value={ef.status} onValueChange={(v) => setEf(x => ({ ...x, status: v }))}>
                  <SelectTrigger data-testid="edit-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">Subtotal, GST, total, paid & due are recalculated on save. Paid invoices cannot be line-edited.</div>
            <Button data-testid="save-edit-btn" onClick={saveEdit} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* List print view (only rendered when no invoice dialog is open) */}
      {!openInv && (
        <div className="hidden" id="print-invoices">
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="text-lg font-bold">{org?.business_name || "SynqMoto ERP Workshop"}</div>
              <div className="text-xs">{org?.establishment_name || org?.branch_name || ""}</div>
            </div>
            <div className="text-right text-xs">
              <div className="font-bold">Sales Invoice Report</div>
              <div>Report Date: {new Date().toISOString().slice(0, 10)}</div>
            </div>
          </div>
          <div className="text-xs mb-3 border-b border-black pb-2">Filters — {filterSummary} · Rows: {list.length}</div>
          <table className="w-full border-collapse" style={{ fontSize: "8px" }}>
            <thead>
              <tr>
                <th rowSpan={2} className="border border-black text-left p-1">Invoice #</th>
                <th rowSpan={2} className="border border-black text-left p-1">Date</th>
                <th rowSpan={2} className="border border-black text-left p-1">Customer</th>
                <th rowSpan={2} className="border border-black text-left p-1">Mobile</th>
                <th rowSpan={2} className="border border-black text-right p-1">Items</th>
                <th colSpan={3} className="border border-black text-center p-1">Amount</th>
                <th colSpan={4} className="border border-black text-center p-1">Paid</th>
                <th rowSpan={2} className="border border-black text-right p-1">Balance</th>
                <th rowSpan={2} className="border border-black text-left p-1">Payment</th>
              </tr>
              <tr>
                <th className="border border-black text-right p-1">Parts</th>
                <th className="border border-black text-right p-1">Labour</th>
                <th className="border border-black text-right p-1">Total</th>
                <th className="border border-black text-right p-1">Cash</th>
                <th className="border border-black text-right p-1">PhonePay</th>
                <th className="border border-black text-right p-1">Paytm</th>
                <th className="border border-black text-right p-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {list.map(inv => (
                <tr key={inv.id}>
                  <td className="border border-black p-1">{inv.invoice_number}</td>
                  <td className="border border-black p-1">{(inv.created_at || "").slice(0, 10)}</td>
                  <td className="border border-black p-1">{inv.customer_name || custName(inv.customer_id)}</td>
                  <td className="border border-black p-1">{inv.customer_phone || "-"}</td>
                  <td className="border border-black p-1 text-right">{inv.items_count || 0}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.parts_total || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.labour_total || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr((inv.parts_total || 0) + (inv.labour_total || 0))}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.paid_cash || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.paid_phonepay || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.paid_paytm || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.paid || 0)}</td>
                  <td className="border border-black p-1 text-right">{inr(inv.due || 0)}</td>
                  <td className="border border-black p-1">{PAY_LABELS[inv.status] || inv.status}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td className="border border-black p-1" colSpan={4}>TOTALS · {list.length} invoices</td>
                <td className="border border-black p-1 text-right">{invTotals.items}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.parts)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.labour)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.total)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.cash)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.phonepay)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.paytm)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.paid)}</td>
                <td className="border border-black p-1 text-right">{inr(invTotals.balance)}</td>
                <td className="border border-black p-1"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <style>{`
        @media print {
          body.printing-inv-list * { visibility: hidden; }
          body.printing-inv-list #print-invoices, body.printing-inv-list #print-invoices * { visibility: visible; }
          body.printing-inv-list #print-invoices { position: absolute; left: 0; top: 0; width: 100%; display: block !important; background: #fff; color: #000; }
        }
      `}</style>
    </div>
  );
}
